// AllCompaniesSettings.jsx
//
// Super-admin-only page at /admin/all-companies-settings. One screen
// to manage every tenant — subscription packages, expiration date,
// users, brands, permissions. Reuses the existing
// /super-admin-panel/companies endpoint (companies + packages) and
// the existing per-company drilldowns (/super-admin/users/:id,
// /super-admin/brands/:id, /super-admin/permissions/:id) via deep
// links so the original screens stay the source of truth for each
// detail page.
//
// Layout:
//   - Top: search + status filter (active/trial/expired/suspended)
//   - Sortable table: Name · Plan · Status · Users · Brands · Exp · Actions
//   - Inline edit: packages (multi-select), expiration (date picker)
//   - Each row has quick-jump buttons to the existing detail pages
//
// Pure UI; everything writes through existing endpoints. No backend
// changes needed beyond what's already shipping.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { get, post, putGeneral } from '../../api';
import addNotification from 'helpers/notify';
import { fieldSetter } from "../../helpers/formEvents";

function fmtDate(d) {
  if (!d) return '—';
  const t = new Date(d);
  if (isNaN(t.getTime())) return '—';
  return t.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function daysUntil(d) {
  if (!d) return null;
  const ms = new Date(d).getTime() - Date.now();
  if (isNaN(ms)) return null;
  return Math.floor(ms / (1000 * 60 * 60 * 24));
}

function statusOf(company) {
  if (company.enabled === false) return 'suspended';
  const dx = daysUntil(company.dateOfExpiration);
  if (dx == null) return 'active';
  if (dx < 0) return 'expired';
  if (company.trial) return dx <= 7 ? `trial · ${dx}d` : 'trial';
  return dx <= 7 ? `expiring · ${dx}d` : 'active';
}

function statusTone(status) {
  if (status.startsWith('expired') || status === 'suspended') return { bg: '#fdecea', fg: '#a71d2a' };
  if (status.startsWith('expiring') || status.startsWith('trial ·')) return { bg: '#fff4cc', fg: '#856404' };
  if (status === 'trial') return { bg: '#eef2ff', fg: '#15172b' };
  return { bg: '#e7f5ec', fg: '#1e6b3e' };
}

export default function AllCompaniesSettings() {
  const history = useHistory();
  const [rows, setRows] = useState([]);
  const [allPackages, setAllPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sort, setSort] = useState({ key: 'companyName', dir: 'asc' });
  const [editing, setEditing] = useState(null); // { id, packages, expiration }

  const refresh = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await get('/super-admin-panel/companies');
      const data = (r && r.data) || {};
      setRows(Array.isArray(data.companies) ? data.companies : []);
      setAllPackages(Array.isArray(data.allPackages) ? data.allPackages : []);
    } catch (e) {
      setErr((e && (e.message || (e.response && e.response.data && e.response.data.error))) || 'Failed to load');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const filtered = useMemo(() => {
    let out = rows;
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      out = out.filter((c) => String(c.companyName || '').toLowerCase().includes(needle));
    }
    if (statusFilter !== 'all') {
      out = out.filter((c) => {
        const s = statusOf(c);
        if (statusFilter === 'active')    return s === 'active';
        if (statusFilter === 'trial')     return s.startsWith('trial');
        if (statusFilter === 'expiring')  return s.startsWith('expiring');
        if (statusFilter === 'expired')   return s === 'expired';
        if (statusFilter === 'suspended') return s === 'suspended';
        return true;
      });
    }
    const k = sort.key;
    const dir = sort.dir === 'asc' ? 1 : -1;
    out = [...out].sort((a, b) => {
      const av = (a[k] || '').toString().toLowerCase();
      const bv = (b[k] || '').toString().toLowerCase();
      if (av < bv) return -dir;
      if (av > bv) return dir;
      return 0;
    });
    return out;
  }, [rows, q, statusFilter, sort]);

  const startEdit = (company) => {
    setEditing({
      id: company._id,
      packages: company.activePackages || [],
      expiration: company.dateOfExpiration
        ? new Date(company.dateOfExpiration).toISOString().slice(0, 10)
        : '',
    });
  };

  const savePackages = async () => {
    if (!editing) return;
    try {
      await putGeneral(`/super-admin-panel/companies/${editing.id}/packages`, { packages: editing.packages });
      addNotification('Subscription packages updated', 'success');
      setEditing(null);
      refresh();
    } catch (e) {
      addNotification((e && e.message) || 'Failed to save packages', 'danger');
    }
  };

  const saveExpiration = async () => {
    if (!editing) return;
    try {
      await post('/company/change-expiration', { company_id: editing.id, dateOfExpiration: editing.expiration }, false);
      addNotification('Expiration date updated', 'success');
      setEditing(null);
      refresh();
    } catch (e) {
      addNotification((e && e.message) || 'Failed to save expiration', 'danger');
    }
  };

  const headerCell = (label, key) => (
    <th
      onClick={() => setSort((s) => ({ key, dir: s.key === key && s.dir === 'asc' ? 'desc' : 'asc' }))}
      style={{ cursor: 'pointer', userSelect: 'none' }}
      title="Click to sort">
      {label}{sort.key === key ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : ''}
    </th>
  );

  return (
    <div style={{ padding: 20, maxWidth: 1320 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h3 style={{ margin: 0 }}>All Companies Settings</h3>
          <div style={{ fontSize: 12, color: '#666' }}>Super-admin view: subscription, users, expiration, brands, and permissions for every tenant.</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search company name…"
            style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13, minWidth: 220 }} />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                  style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13 }}>
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="trial">Trial</option>
            <option value="expiring">Expiring soon</option>
            <option value="expired">Expired</option>
            <option value="suspended">Suspended</option>
          </select>
          <button
            onClick={() => history.push('/admin/platform-config')}
            className="acc-btn"
            style={{ fontSize: 12, background: '#15172b', color: '#fff', border: '1px solid #15172b' }}
            title="Manage platform environment variables (LLM keys, Stripe, SMTP, OAuth, schedulers, …)"
          >
            Environment Variables
          </button>
          <button onClick={refresh} className="acc-btn" style={{ fontSize: 12 }}>Refresh</button>
        </div>
      </div>

      {err && <div className="alert alert-warning" style={{ fontSize: 13 }}>{err}</div>}

      <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 8, overflow: 'hidden' }}>
        <div style={{ padding: '10px 14px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#666' }}>
          <div>{loading ? 'Loading…' : `${filtered.length} of ${rows.length} companies`}</div>
          <div>{allPackages.length} packages defined</div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="table table-hover" style={{ fontSize: 13, marginBottom: 0 }}>
            <thead>
              <tr style={{ background: '#fafbfc' }}>
                {headerCell('Company', 'companyName')}
                <th>Subscription</th>
                <th>Status</th>
                {headerCell('Permissions', 'permCount')}
                {headerCell('Expiration', 'dateOfExpiration')}
                <th style={{ minWidth: 220 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const s = statusOf(c);
                const tone = statusTone(s);
                const isEditing = editing && editing.id === c._id;
                return (
                  <React.Fragment key={c._id}>
                    <tr>
                      <td>
                        <strong>{c.companyName}</strong>
                        <div style={{ fontSize: 11, color: '#888' }}>{c._id}</div>
                      </td>
                      <td>
                        {(c.activePackages || []).length === 0 ? (
                          <span style={{ color: '#888', fontSize: 12 }}>none</span>
                        ) : (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {(c.activePackages || []).map((p) => (
                              <span key={p} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: '#eef2ff', color: '#15172b' }}>{p}</span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td>
                        <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, background: tone.bg, color: tone.fg, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>{s}</span>
                      </td>
                      <td>{c.permCount}</td>
                      <td>{fmtDate(c.dateOfExpiration)}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          <button className="acc-btn" style={{ fontSize: 11, padding: '3px 8px' }} onClick={() => startEdit(c)}>
                            {isEditing ? 'Cancel' : 'Edit'}
                          </button>
                          <button className="acc-btn" style={{ fontSize: 11, padding: '3px 8px' }}
                                  onClick={() => history.push(`/super-admin/users/${c._id}`)}>
                            Users
                          </button>
                          <button className="acc-btn" style={{ fontSize: 11, padding: '3px 8px' }}
                                  onClick={() => history.push(`/super-admin/brands/${c._id}`)}>
                            Brands
                          </button>
                          <button className="acc-btn" style={{ fontSize: 11, padding: '3px 8px' }}
                                  onClick={() => history.push(`/super-admin/permissions/${c._id}`)}>
                            Permissions
                          </button>
                        </div>
                      </td>
                    </tr>

                    {isEditing && (
                      <tr style={{ background: '#fafbfc' }}>
                        <td colSpan={6} style={{ padding: 14 }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24 }}>
                            {/* Subscription packages */}
                            <div>
                              <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 8 }}>Subscription packages</div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {allPackages.map((p) => {
                                  const on = editing.packages.includes(p);
                                  return (
                                    <label key={p} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                                      <input type="checkbox" checked={on}
                                             onChange={() => {
                                               setEditing((e) => ({
                                                 ...e,
                                                 packages: on
                                                   ? e.packages.filter((x) => x !== p)
                                                   : [...e.packages, p],
                                               }));
                                             }} />
                                      {p}
                                    </label>
                                  );
                                })}
                              </div>
                              <div style={{ marginTop: 10 }}>
                                <button className="acc-btn acc-btn-primary" style={{ fontSize: 12 }} onClick={savePackages}>Save packages</button>
                                <small style={{ marginLeft: 8, color: '#888', fontSize: 11 }}>
                                  Each package maps to a permission set in config.Packages — saving overwrites the company's permission row with the union.
                                </small>
                              </div>
                            </div>

                            {/* Expiration date */}
                            <div>
                              <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 8 }}>Expiration date</div>
                              <input
                                type="date"
                                value={editing.expiration}
                                onChange={fieldSetter(setEditing, 'expiration')}
                                style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13, width: '100%' }} />
                              <button className="acc-btn acc-btn-primary" style={{ fontSize: 12, marginTop: 10 }} onClick={saveExpiration}>Save expiration</button>
                              <div style={{ fontSize: 11, color: '#888', marginTop: 6 }}>
                                Past-dated companies hit a 402 mid-session via requireActiveSubscription. Trial banner reads the same field.
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', padding: 30, color: '#888' }}>
                    No companies match the current filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Exposed for unit tests on the pure helpers.
AllCompaniesSettings._test = { fmtDate, daysUntil, statusOf, statusTone };
